import { teamBestNet } from '@/lib/games/formats/team-formats';
import { computeNassau, type SkinsPlayer } from '@/lib/games/scoring';
import {
  groupRelativePlayingHandicap,
  netScoresFromGross,
  playingHandicap,
} from '@/lib/games/handicap';
import { strokeIndexes } from './helpers';
import type { GameInstanceConfig, NassauMatchConfig, RoundContext } from './types';
import { getHandicapAllowance, getLegStakes, getScoringMode, getStakeConfig } from './stakes-helpers';

export const NASSAU_FORMAT_IDS = new Set(['nassau', 'team-nassau']);

export function isNassauFormat(formatId: string): boolean {
  return NASSAU_FORMAT_IDS.has(formatId);
}

function newMatchId(): string {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Default single matchup when none configured yet. */
export function defaultNassauMatches(
  formatId: string,
  playerKeys: string[],
  teams?: string[][],
  legStakes?: { front: number; back: number; total: number },
): NassauMatchConfig[] {
  const stakes = legStakes;
  if (formatId === 'team-nassau' && teams && teams.length >= 2) {
    return [{
      id: newMatchId(),
      mode: 'teams',
      sideA: [...teams[0]],
      sideB: [...teams[1]],
      stakes,
    }];
  }
  if (playerKeys.length >= 2) {
    return [{
      id: newMatchId(),
      mode: 'singles',
      sideA: [playerKeys[0]],
      sideB: [playerKeys[1]],
      stakes,
    }];
  }
  return [];
}

/** Foursome singles: each unique pair (3 matches). */
export function roundRobinNassauMatches(
  playerKeys: string[],
  legStakes?: { front: number; back: number; total: number },
): NassauMatchConfig[] {
  const out: NassauMatchConfig[] = [];
  for (let i = 0; i < playerKeys.length; i += 1) {
    for (let j = i + 1; j < playerKeys.length; j += 1) {
      out.push({
        id: newMatchId(),
        mode: 'singles',
        sideA: [playerKeys[i]],
        sideB: [playerKeys[j]],
        stakes: legStakes,
      });
    }
  }
  return out;
}

/** Migrate legacy opponentKey or build defaults. */
export function normalizeNassauMatches(
  formatId: string,
  config: GameInstanceConfig,
  ctx: RoundContext,
  defaultLegStakes?: { front: number; back: number; total: number },
): NassauMatchConfig[] {
  if (config.matches?.length) return config.matches;

  const keys = ctx.players.map((p) => p.playerKey);
  if (formatId === 'team-nassau' && ctx.teams && ctx.teams.length >= 2) {
    return defaultNassauMatches(formatId, keys, ctx.teams, defaultLegStakes);
  }

  const oppKey = config.opponentKey;
  if (oppKey && keys.includes(oppKey)) {
    const sideA = keys.find((k) => k !== oppKey) || keys[0];
    return [{
      id: 'legacy-1',
      mode: 'singles',
      sideA: [sideA],
      sideB: [oppKey],
      stakes: defaultLegStakes,
    }];
  }

  return defaultNassauMatches(formatId, keys, ctx.teams, defaultLegStakes);
}

function buildSinglesPlayer(
  ctx: RoundContext,
  playerKey: string,
  formatId: string,
): SkinsPlayer | null {
  const p = ctx.players.find((x) => x.playerKey === playerKey);
  if (!p) return null;
  const gross = ctx.scores[playerKey] || Array(ctx.holes.length).fill(null);
  const sis = strokeIndexes(ctx.holes);
  const allowance = getHandicapAllowance(getStakeConfig(ctx.stakes, formatId), formatId);
  const ph = ctx.players.length >= 2
    ? groupRelativePlayingHandicap(ctx.players, p, formatId, allowance)
    : playingHandicap(p.courseHcp, formatId, allowance);
  const net = netScoresFromGross(gross, ph, sis);
  return { id: playerKey, gross, net };
}

function buildTeamSidePlayer(
  ctx: RoundContext,
  sideKeys: string[],
  formatId: string,
): SkinsPlayer {
  const holeCount = ctx.holes.length;
  const gross: (number | null)[] = Array(holeCount).fill(null);
  const net: (number | null)[] = Array(holeCount).fill(null);
  for (let i = 0; i < holeCount; i += 1) {
    const best = teamBestNet(sideKeys, i, ctx.players, ctx.holes, ctx.scores, 'fourball');
    gross[i] = best;
    net[i] = best;
  }
  return { id: sideKeys.join('+'), gross, net };
}

export interface NassauMatchResult {
  match: NassauMatchConfig;
  label: string;
  summary: string;
  netCents: Record<string, number>;
  winner?: string;
}

export function computeNassauMatch(
  ctx: RoundContext,
  formatId: string,
  match: NassauMatchConfig,
): NassauMatchResult | null {
  const stake = getStakeConfig(ctx.stakes, formatId);
  const legs = match.stakes ?? getLegStakes(stake);
  const mode = getScoringMode(stake);

  let p1: SkinsPlayer | null;
  let p2: SkinsPlayer | null;
  if (match.mode === 'teams') {
    p1 = buildTeamSidePlayer(ctx, match.sideA, formatId);
    p2 = buildTeamSidePlayer(ctx, match.sideB, formatId);
  } else {
    p1 = buildSinglesPlayer(ctx, match.sideA[0], formatId);
    p2 = buildSinglesPlayer(ctx, match.sideB[0], formatId);
  }
  if (!p1 || !p2) return null;

  const result = computeNassau(p1, p2, mode, legs.total);
  const netCents: Record<string, number> = {};
  const allKeys = [...match.sideA, ...match.sideB];
  allKeys.forEach((k) => { netCents[k] = 0; });

  const creditSide = (sideKeys: string[], amount: number) => {
    const per = amount / sideKeys.length;
    sideKeys.forEach((k) => { netCents[k] = (netCents[k] || 0) + per; });
  };
  const debitSide = (sideKeys: string[], amount: number) => {
    const per = amount / sideKeys.length;
    sideKeys.forEach((k) => { netCents[k] = (netCents[k] || 0) - per; });
  };

  const applyLeg = (leg: typeof result.front, bet: number) => {
    if (!leg.winner) return;
    if (leg.winner === p1.id) {
      creditSide(match.sideA, bet * 100);
      debitSide(match.sideB, bet * 100);
    } else if (leg.winner === p2.id) {
      creditSide(match.sideB, bet * 100);
      debitSide(match.sideA, bet * 100);
    }
  };
  applyLeg(result.front, legs.front);
  applyLeg(result.back, legs.back);
  applyLeg(result.total, legs.total);

  const nameA = match.sideA.map((k) => ctx.players.find((p) => p.playerKey === k)?.name.split(' ')[0] || 'Player').join(' & ');
  const nameB = match.sideB.map((k) => ctx.players.find((p) => p.playerKey === k)?.name.split(' ')[0] || 'Player').join(' & ');
  const diff = result.total.diff;
  const legSummary = result.total.winner === p1.id
    ? `${nameA} ${diff} up overall`
    : result.total.winner === p2.id
      ? `${nameB} ${diff} up overall`
      : 'All square overall';

  const label = match.label || `${nameA} vs ${nameB}`;
  const winnerKeys = result.total.winner === p1.id ? match.sideA
    : result.total.winner === p2.id ? match.sideB : undefined;

  return {
    match,
    label,
    summary: legSummary,
    netCents,
    winner: winnerKeys?.[0],
  };
}

export function computeAllNassauMatches(
  ctx: RoundContext,
  formatId: string,
  config: GameInstanceConfig,
): NassauMatchResult[] {
  const stake = getStakeConfig(ctx.stakes, formatId);
  const defaultLegs = getLegStakes(stake);
  const matches = normalizeNassauMatches(formatId, config, ctx, defaultLegs);
  return matches
    .map((m) => computeNassauMatch(ctx, formatId, m))
    .filter((r): r is NassauMatchResult => r != null);
}
